import { useCallback, useEffect, useMemo, useState } from 'react'
import { ConfigVersionHistoryButton } from '../components/ConfigVersionHistory'
import { Link } from 'react-router-dom'
import {
  RETRY_ACTIONS,
  ABSENCE_REASONS,
  CUSTOMER_TYPE_LABEL,
  QUESTION_TYPES,
  WEEKDAYS,
  callCenterSetupApi,
  type AbsenceReason,
  type AgentAvailabilityOverview,
  type CallScript,
  type CallScriptBody,
  type CustomerType,
  type LeadRetryPolicy,
  type LeadRetryPolicyList,
  type LeadRetryPreview,
  type LeadSpeedTargets,
  type RetryExhaustionAction,
  type QualificationForm,
  type QualificationQuestion,
  type QuestionType,
  type TimeSlot,
  type WeeklySchedule,
  type WeekdayKey,
} from '../api/callCenterSetup'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { fetchOperators, fetchAllProducts, type Operator, type Product } from '../api/catalog'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  Page,
  PageHeader,
  Tabs,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  useSnackbar,
  type PillTone,
  useConfirm,
  usePrompt,
  FormSection,
  FormGrid,
} from '../components/ui'

/** F-100: avviso mostrato dopo una clonazione (la copia nasce inattiva o in bozza e va attivata a mano). */
const CLONE_NOTICE = 'Copia creata: è inattiva (in bozza). Controllala e attivala quando è pronta.'

function CloneNotice({ text, onClose }: { text: string; onClose: () => void }) {
  if (!text) return null
  return (
    <Callout
      tone="info"
      action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={onClose} />}
    >
      {text}
    </Callout>
  )
}

/**
 * F-088 (TASK-0342): configurazione del call center, solo Admin (la scrittura sul server è PolicyNames.Admin).
 * Tre schede: Script (per campagna, settore e tipo cliente), Questionari di qualificazione (con versioni) e
 * Disponibilità degli operatori (orario settimanale in ora di Roma e assenze).
 * Dichiarato: senza orario un operatore è sempre disponibile; l'area geografica non esiste perché il lead non ha
 * la provincia; la lista degli operatori di una campagna è informativa (la coda resta comune).
 */
type Tab = 'scripts' | 'forms' | 'availability' | 'retries' | 'speed'

const TABS: { key: Tab; label: string }[] = [
  { key: 'scripts', label: 'Script' },
  { key: 'forms', label: 'Questionari' },
  { key: 'availability', label: 'Disponibilità' },
  { key: 'retries', label: 'Ritentativi' },
  { key: 'speed', label: 'Tempi di contatto' },
]

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function Modal({ title, supportingText, onClose, onSave, saving, error, children }: {
  title: string
  supportingText?: React.ReactNode
  onClose: () => void
  onSave: () => void
  saving: boolean
  error: string
  children: React.ReactNode
}) {
  return (
    <Dialog
      as="form"
      onSubmit={(e) => { e.preventDefault(); onSave() }}
      open
      onClose={onClose}
      title={title}
      supportingText={supportingText}
      actions={
        <>
          <Button variant="text" onClick={onClose}>Annulla</Button>
          <Button type="submit" variant="text" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </>
      }
    >
      <>
        {error && <Callout tone="error">{error}</Callout>}
        {children}
      </>
    </Dialog>
  )
}

function useUsers() {
  const [users, setUsers] = useState<DirectoryUser[]>([])
  useEffect(() => {
    let alive = true
    userDirectoryApi.list({ limit: 200 })
      .then(r => { if (alive) setUsers(r) })
      .catch(() => { if (alive) setUsers([]) })
    return () => { alive = false }
  }, [])
  const label = useMemo(() => {
    const map = new Map<number, string>()
    users.forEach(u => map.set(u.id, u.name))
    return (id: number) => map.get(id) ?? `Utente #${id}`
  }, [users])
  return { users, label }
}

function useCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  useEffect(() => {
    let alive = true
    campaignsApi.list({ pageSize: 200 })
      .then(r => { if (alive) setCampaigns(r.items) })
      .catch(() => { if (alive) setCampaigns([]) })
    return () => { alive = false }
  }, [])
  const label = useMemo(() => {
    const map = new Map<number, string>()
    campaigns.forEach(c => map.set(c.id, c.name))
    return (id: number) => map.get(id) ?? `Campagna #${id}`
  }, [campaigns])
  return { campaigns, label }
}

// ------------------------------------------------------------------- Script

interface ScriptForm {
  name: string
  body: string
  campaignId: number | ''
  sector: string
  customerType: CustomerType | ''
  // F-143 (REQ-092-001): operatore e prodotto del catalogo ('' = qualsiasi).
  catalogOperatorId: number | ''
  catalogProductId: number | ''
  priority: number
  isActive: boolean
}

const EMPTY_SCRIPT: ScriptForm = {
  name: '', body: '', campaignId: '', sector: '', customerType: '', catalogOperatorId: '', catalogProductId: '',
  priority: 0, isActive: true,
}

function ScriptsTab() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const { campaigns, label: campaignLabel } = useCampaigns()
  const [items, setItems] = useState<CallScript[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<ScriptForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  // F-143 (REQ-092-001): prodotti e operatori del catalogo per i due selettori.
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([])
  const [catalogOperators, setCatalogOperators] = useState<Operator[]>([])
  useEffect(() => {
    fetchAllProducts().then(r => setCatalogProducts(r.items)).catch(() => setCatalogProducts([]))
    fetchOperators().then(r => setCatalogOperators(r.items)).catch(() => setCatalogOperators([]))
  }, [])
  const productName = (id: number | null | undefined) => catalogProducts.find(p => p.id === id)?.name ?? `Prodotto #${id}`
  const operatorName = (id: number | null | undefined) => catalogOperators.find(o => o.id === id)?.name ?? `Operatore #${id}`

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await callCenterSetupApi.scripts.list())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  function openEdit(s: CallScript) {
    setForm({
      name: s.name, body: s.body, campaignId: s.campaignId ?? '', sector: s.sector ?? '',
      customerType: s.customerType ?? '', catalogOperatorId: s.catalogOperatorId ?? '',
      catalogProductId: s.catalogProductId ?? '', priority: s.priority, isActive: s.isActive,
    })
    setEditId(s.id)
    setSaveError('')
  }

  // F-100: dopo "Clona" la copia (inattiva) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(items, s => openEdit(s))

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    if (!form.body.trim()) { setSaveError('Il testo dello script è obbligatorio'); return }
    if (form.campaignId === '' && !form.sector.trim() && form.customerType === ''
      && form.catalogOperatorId === '' && form.catalogProductId === '') {
      setSaveError('Indica almeno un criterio: campagna, prodotto, operatore, settore o tipo cliente')
      return
    }
    const body: CallScriptBody = {
      name: form.name.trim(),
      body: form.body,
      campaignId: form.campaignId === '' ? null : form.campaignId,
      sector: form.sector.trim() || null,
      customerType: form.customerType === '' ? null : form.customerType,
      catalogOperatorId: form.catalogOperatorId === '' ? null : form.catalogOperatorId,
      catalogProductId: form.catalogProductId === '' ? null : form.catalogProductId,
      priority: form.priority,
      isActive: form.isActive,
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await callCenterSetupApi.scripts.update(editId, body)
      else await callCenterSetupApi.scripts.create(body)
      setForm(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(s: CallScript) {
    if (!(await askConfirm({ title: 'Elimina script', message: `Eliminare lo script "${s.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await callCenterSetupApi.scripts.remove(s.id)
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, "Errore durante l'eliminazione"), tone: 'error' })
    }
  }

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="text-muted md-typescale-body-small">
          A un lead si propone lo script più specifico che corrisponde: prima la campagna, poi il prodotto, poi
          l'operatore, il settore e il tipo cliente; a parità vince la priorità più alta. Senza corrispondenze si usa il testo libero dello script della
          campagna.
        </div>
        <Button variant="tonal" size="sm" onClick={() => { setForm(EMPTY_SCRIPT); setEditId(null); setSaveError('') }}>
          Nuovo script
        </Button>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <CloneNotice text={cloneNotice} onClose={() => setCloneNotice('')} />
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState icon="description" title="Nessuno script configurato" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Campagna</th><th>Prodotto</th><th>Operatore</th><th>Settore</th><th>Tipo cliente</th>
                <th>Priorità</th><th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id}>
                  <td className="md-typescale-emphasized-body-medium">{s.name}</td>
                  <td>{s.campaignId !== null ? campaignLabel(s.campaignId) : <span className="text-muted">Tutte</span>}</td>
                  <td>{s.catalogProductId ? productName(s.catalogProductId) : <span className="text-muted">Tutti</span>}</td>
                  <td>{s.catalogOperatorId ? operatorName(s.catalogOperatorId) : <span className="text-muted">Tutti</span>}</td>
                  <td>{s.sector ?? <span className="text-muted">Tutti</span>}</td>
                  <td>{s.customerType ? CUSTOMER_TYPE_LABEL[s.customerType] : <span className="text-muted">Tutti</span>}</td>
                  <td className="text-muted md-num">{s.priority}</td>
                  <td>{s.isActive ? <StatusPill tone="success">Attivo</StatusPill> : <StatusPill tone="neutral">Inattivo</StatusPill>}</td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <CloneConfigurationButton
                        entityLabel="script"
                        url={`/api/call-scripts/${s.id}/clone`}
                        defaultName={s.name}
                        note="Si copiano testo, ambito e priorità. La copia nasce inattiva."
                        onCloned={async id => { await load(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />
                      <ConfigVersionHistoryButton entityType="CallScript" entityId={s.id} label={s.name} />
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(s)} />
                      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void remove(s)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {form && (
        <Modal title={editId !== null ? 'Modifica script' : 'Nuovo script'} saving={saving} error={saveError}
          onClose={() => { setForm(null); setEditId(null) }} onSave={() => void save()}>
          <FormSection title="Script">
            <TextField className="md-span-full" label="Nome *" value={form.name} maxLength={100}
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <TextField label="Priorità" type="number" value={form.priority}
              onChange={e => setForm(f => f && ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))} />
          </FormSection>
          <FormSection title="Quando si applica">
            <Select label="Campagna" value={form.campaignId}
              onChange={e => setForm(f => f && ({ ...f, campaignId: e.target.value ? parseInt(e.target.value, 10) : '' }))}>
              <option value="">Tutte</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Select label="Prodotto di catalogo" value={form.catalogProductId}
              onChange={e => setForm(f => f && ({ ...f, catalogProductId: e.target.value ? parseInt(e.target.value, 10) : '' }))}>
              <option value="">Tutti</option>
              {catalogProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <Select label="Operatore di catalogo" value={form.catalogOperatorId}
              supportingText="Se indichi anche un prodotto, deve essere di questo operatore."
              onChange={e => setForm(f => f && ({ ...f, catalogOperatorId: e.target.value ? parseInt(e.target.value, 10) : '' }))}>
              <option value="">Tutti</option>
              {catalogOperators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <TextField label="Settore" value={form.sector} maxLength={100} supportingText="Es. Energia (vuoto = tutti)"
              onChange={e => setForm(f => f && ({ ...f, sector: e.target.value }))} />
            <Select label="Tipo cliente" value={form.customerType}
              onChange={e => setForm(f => f && ({ ...f, customerType: e.target.value as CustomerType | '' }))}>
              <option value="">Tutti</option>
              <option value="person">{CUSTOMER_TYPE_LABEL.person}</option>
              <option value="company">{CUSTOMER_TYPE_LABEL.company}</option>
            </Select>
          </FormSection>
          <FormSection title="Testo" columns={1}>
            <TextField className="md-span-full" label="Testo dello script *" multiline rows={10} maxLength={20000} value={form.body}
              supportingText={`${form.body.length} / 20000 caratteri. Almeno un criterio fra campagna, prodotto, operatore, settore e tipo cliente.`}
              onChange={e => setForm(f => f && ({ ...f, body: e.target.value }))} />
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox className="md-span-full" id="cs-active" label="Attivo" checked={form.isActive}
              onChange={e => setForm(f => f && ({ ...f, isActive: e.target.checked }))} />
          </FormSection>
        </Modal>
      )}
    </>
  )
}

// ---------------------------------------------------------------- Questionari

interface QuestionRow {
  code: string
  label: string
  type: QuestionType
  options: string
  required: boolean
  showCode: string
  showEquals: string
}

interface FormEditor {
  name: string
  sector: string
  customerType: CustomerType | ''
  questions: QuestionRow[]
}

const EMPTY_QUESTION: QuestionRow = {
  code: '', label: '', type: 'Testo', options: '', required: false, showCode: '', showEquals: '',
}

function toRows(questions: QualificationQuestion[]): QuestionRow[] {
  return questions.map(q => ({
    code: q.code,
    label: q.label,
    type: q.type,
    options: (q.options ?? []).join(', '),
    required: q.required ?? false,
    showCode: q.showIf?.code ?? '',
    showEquals: q.showIf?.equals ?? '',
  }))
}

function fromRows(rows: QuestionRow[]): QualificationQuestion[] {
  return rows.map(r => {
    const q: QualificationQuestion = { code: r.code.trim(), label: r.label.trim(), type: r.type, required: r.required }
    if (r.type === 'Elenco') q.options = r.options.split(',').map(o => o.trim()).filter(o => o.length > 0)
    if (r.showCode) q.showIf = { code: r.showCode, equals: r.showEquals.trim() }
    return q
  })
}

const STATUS_TONE: Record<string, PillTone> = { Bozza: 'warning', Attivo: 'success', Archiviato: 'neutral' }

function FormsTab() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<QualificationForm[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [editor, setEditor] = useState<FormEditor | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await callCenterSetupApi.forms.list())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  function openNew() {
    setEditor({ name: '', sector: '', customerType: '', questions: [{ ...EMPTY_QUESTION }] })
    setEditId(null)
    setSaveError('')
  }

  function openEdit(f: QualificationForm) {
    setEditor({ name: f.name, sector: f.sector ?? '', customerType: f.customerType ?? '', questions: toRows(f.questions) })
    setEditId(f.id)
    setSaveError('')
  }

  // F-100: dopo "Clona" la copia (Bozza v1) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(items, f => openEdit(f))

  async function save() {
    if (!editor) return
    if (!editor.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    if (editor.questions.length === 0) { setSaveError('Aggiungi almeno una domanda'); return }
    const body = {
      name: editor.name.trim(),
      sector: editor.sector.trim() || null,
      customerType: editor.customerType === '' ? null : editor.customerType,
      questions: fromRows(editor.questions),
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await callCenterSetupApi.forms.update(editId, body)
      else await callCenterSetupApi.forms.create(body)
      setEditor(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function act(action: () => Promise<unknown>, confirm?: string) {
    if (confirm && !(await askConfirm({ title: 'Conferma azione', message: confirm, confirmLabel: 'Conferma' }))) return
    try {
      await action()
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, 'Operazione non riuscita'), tone: 'error' })
    }
  }

  function updateRow(idx: number, patch: Partial<QuestionRow>) {
    setEditor(ed => ed && ({ ...ed, questions: ed.questions.map((q, i) => i === idx ? { ...q, ...patch } : q) }))
  }

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="text-muted md-typescale-body-small">
          Un questionario si modifica finché è in bozza. Una volta attivo non cambia più: per modificarlo si crea una
          nuova versione, e attivarla archivia la precedente. Le risposte già raccolte restano legate alla versione a
          cui rispondevano.
        </div>
        <Button variant="tonal" size="sm" onClick={openNew}>Nuovo questionario</Button>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <CloneNotice text={cloneNotice} onClose={() => setCloneNotice('')} />
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState icon="quiz" title="Nessun questionario configurato" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Versione</th><th>Settore</th><th>Tipo cliente</th>
                <th>Domande</th><th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id}>
                  <td className="md-typescale-emphasized-body-medium">{f.name}</td>
                  <td className="md-num">v{f.version}</td>
                  <td>{f.sector ?? <span className="text-muted">Tutti</span>}</td>
                  <td>{f.customerType ? CUSTOMER_TYPE_LABEL[f.customerType] : <span className="text-muted">Tutti</span>}</td>
                  <td className="md-num">{f.questions.length}</td>
                  <td><StatusPill tone={STATUS_TONE[f.status] ?? 'neutral'}>{f.status}</StatusPill></td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <CloneConfigurationButton
                        entityLabel="questionario"
                        url={`/api/lead-qualification-forms/${f.id}/clone`}
                        defaultName={f.name}
                        note="Si copiano domande, settore e tipo cliente. La copia nasce in bozza, versione 1, con il nuovo nome."
                        onCloned={async id => { await load(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />
                      {f.status === 'Bozza' && (
                        <>
                          <Button variant="outlined" size="sm" onClick={() => openEdit(f)}>Modifica</Button>
                          <Button variant="outlined" size="sm"
                            onClick={() => void act(() => callCenterSetupApi.forms.activate(f.id),
                              `Attivare "${f.name}" v${f.version}? Da quel momento non si potrà più modificare.`)}>
                            Attiva
                          </Button>
                          <Button variant="outlined" size="sm"
                            onClick={() => void act(() => callCenterSetupApi.forms.remove(f.id), `Eliminare la bozza "${f.name}" v${f.version}?`)}>
                            Elimina
                          </Button>
                        </>
                      )}
                      {f.status !== 'Bozza' && (
                        <Button variant="outlined" size="sm"
                          onClick={() => void act(() => callCenterSetupApi.forms.newVersion(f.id))}>
                          Nuova versione
                        </Button>
                      )}
                      {f.status === 'Attivo' && (
                        <Button variant="outlined" size="sm"
                          onClick={() => void act(() => callCenterSetupApi.forms.archive(f.id), `Archiviare "${f.name}" v${f.version}?`)}>
                          Archivia
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {editor && (
        <Modal title={editId !== null ? 'Modifica bozza del questionario' : 'Nuovo questionario'} saving={saving} error={saveError}
          onClose={() => { setEditor(null); setEditId(null) }} onSave={() => void save()}>
          <FormSection title="Questionario">
            <TextField className="md-span-full" label="Nome *" value={editor.name} maxLength={100} disabled={editId !== null}
              onChange={e => setEditor(ed => ed && ({ ...ed, name: e.target.value }))} />
            <TextField label="Settore" value={editor.sector} maxLength={100} supportingText="Vuoto = tutti"
              onChange={e => setEditor(ed => ed && ({ ...ed, sector: e.target.value }))} />
            <Select label="Tipo cliente" value={editor.customerType}
              onChange={e => setEditor(ed => ed && ({ ...ed, customerType: e.target.value as CustomerType | '' }))}>
              <option value="">Tutti</option>
              <option value="person">{CUSTOMER_TYPE_LABEL.person}</option>
              <option value="company">{CUSTOMER_TYPE_LABEL.company}</option>
            </Select>
          </FormSection>
          <FormSection title="Domande (al massimo 50)" columns={1}>
            {editor.questions.map((q, idx) => (
              <Card as="div" variant="filled" padding="sm" key={idx}>
                <FormGrid>
                  <TextField label="Codice (es. pod)" labelHidden placeholder="Codice (es. pod)" value={q.code} maxLength={40}
                    onChange={e => updateRow(idx, { code: e.target.value })} />
                  <Select label="Tipo di domanda" labelHidden value={q.type}
                    onChange={e => updateRow(idx, { type: e.target.value as QuestionType })}>
                    {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                  <div className="md-span-full d-flex gap-2 align-items-start">
                    <TextField className="flex-grow-1" label="Testo della domanda" labelHidden placeholder="Testo della domanda" value={q.label} maxLength={200}
                      onChange={e => updateRow(idx, { label: e.target.value })} />
                    <IconButton icon="close" size="sm" aria-label="Rimuovi domanda"
                      onClick={() => setEditor(ed => ed && ({ ...ed, questions: ed.questions.filter((_, i) => i !== idx) }))} />
                  </div>
                  {q.type === 'Elenco' && (
                    <TextField className="md-span-full" label="Opzioni separate da virgola" labelHidden placeholder="Opzioni separate da virgola" value={q.options}
                      onChange={e => updateRow(idx, { options: e.target.value })} />
                  )}
                  <Checkbox className="md-span-full" id={`q-req-${idx}`} label="Obbligatoria" checked={q.required}
                    onChange={e => updateRow(idx, { required: e.target.checked })} />
                  <Select label="Visibilità" labelHidden value={q.showCode}
                    onChange={e => updateRow(idx, { showCode: e.target.value })}>
                    <option value="">Sempre visibile</option>
                    {editor.questions.slice(0, idx).filter(p => p.code.trim()).map(p => (
                      <option key={p.code} value={p.code.trim()}>Visibile solo se «{p.code.trim()}» è…</option>
                    ))}
                  </Select>
                  <TextField label="…uguale a (es. Si)" labelHidden placeholder="…uguale a (es. Si)" value={q.showEquals}
                    disabled={!q.showCode} onChange={e => updateRow(idx, { showEquals: e.target.value })} />
                </FormGrid>
              </Card>
            ))}
            <div>
              <Button variant="outlined" size="sm" disabled={editor.questions.length >= 50}
                onClick={() => setEditor(ed => ed && ({ ...ed, questions: [...ed.questions, { ...EMPTY_QUESTION }] }))}>
                Aggiungi domanda
              </Button>
            </div>
            <div className="md-typescale-body-small text-muted">
              Una domanda condizionale può dipendere solo da una domanda che la precede. Se la condizione non vale la
              domanda resta nascosta e la sua risposta viene ignorata.
            </div>
          </FormSection>
        </Modal>
      )}
    </>
  )
}

// -------------------------------------------------------------- Disponibilità

function todayIso(): string {
  // Giorno di Roma (formato AAAA-MM-GG), non quello UTC.
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' })
}

function AvailabilityTab() {
  const { users, label: userLabel } = useUsers()
  const [overview, setOverview] = useState<AgentAvailabilityOverview>({ availabilities: [], absences: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [userId, setUserId] = useState<number | ''>('')
  const [schedule, setSchedule] = useState<WeeklySchedule>({})
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [absence, setAbsence] = useState<{ from: string; to: string; reason: AbsenceReason }>({
    from: todayIso(), to: todayIso(), reason: 'Ferie',
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setOverview(await callCenterSetupApi.availability.overview())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  function pickUser(id: number | '') {
    setUserId(id)
    setNotice('')
    const row = id === '' ? undefined : overview.availabilities.find(a => a.userId === id)
    setSchedule(row?.weeklySchedule ?? {})
  }

  function setSlots(day: WeekdayKey, slots: TimeSlot[]) {
    setSchedule(s => ({ ...s, [day]: slots }))
  }

  async function saveSchedule() {
    if (userId === '') return
    setSaving(true)
    setNotice('')
    try {
      const clean: WeeklySchedule = {}
      for (const d of WEEKDAYS) {
        const slots = (schedule[d.key] ?? []).filter(s => s.from && s.to)
        if (slots.length > 0) clean[d.key] = slots
      }
      await callCenterSetupApi.availability.setSchedule(userId, clean)
      setNotice(Object.keys(clean).length === 0
        ? 'Nessuna fascia: l’operatore è sempre disponibile.'
        : 'Orario salvato.')
      void load()
    } catch (e) {
      setNotice(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function addAbsence() {
    if (userId === '') return
    setNotice('')
    try {
      await callCenterSetupApi.availability.addAbsence({ userId, ...absence })
      void load()
    } catch (e) {
      setNotice(errorText(e, 'Errore durante il salvataggio'))
    }
  }

  async function removeAbsence(id: number) {
    try {
      await callCenterSetupApi.availability.removeAbsence(id)
      void load()
    } catch (e) {
      setNotice(errorText(e, "Errore durante l'eliminazione"))
    }
  }

  const userAbsences = overview.absences.filter(a => userId === '' || a.userId === userId)

  return (
    <>
      <div className="text-muted md-typescale-body-small">
        Gli orari sono in ora di Roma. Chi non ha un orario è sempre disponibile; un operatore in ferie o fuori orario
        non viene scelto nell'instradamento a gruppo (se nessuno è disponibile si usa l'agente di riserva della regola).
        Le assenze valgono per giorni interi, estremi inclusi.
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined">
        <div className="d-flex flex-column gap-3">
          <FormGrid>
            <div>
              <Select label="Operatore" value={userId}
                onChange={e => pickUser(e.target.value ? parseInt(e.target.value, 10) : '')}>
                <option value="">Seleziona…</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name}{overview.availabilities.some(a => a.userId === u.id) ? ' (orario configurato)' : ''}
                  </option>
                ))}
              </Select>
            </div>
          </FormGrid>

          {userId !== '' && (
            <>
              <h3 className="md-typescale-title-medium mb-0">Orario settimanale</h3>
              {WEEKDAYS.map(d => {
                const slots = schedule[d.key] ?? []
                return (
                  <FormGrid columns={4} key={d.key} className="md-form-grid--center">
                    <div className="md-typescale-emphasized-body-medium">{d.label}</div>
                    <div className="md-span-3 d-flex flex-wrap gap-2 align-items-center">
                      {slots.length === 0 && <span className="text-muted md-typescale-body-small">Nessuna fascia</span>}
                      {slots.map((s, i) => (
                        <span className="d-inline-flex align-items-center gap-1" key={i}>
                          <span className="md-u-w-140">
                            <TextField label={`${d.label}: inizio fascia`} labelHidden type="time" value={s.from}
                              onChange={e => setSlots(d.key, slots.map((x, j) => j === i ? { ...x, from: e.target.value } : x))} />
                          </span>
                          –
                          <span className="md-u-w-140">
                            <TextField label={`${d.label}: fine fascia`} labelHidden type="time" value={s.to}
                              onChange={e => setSlots(d.key, slots.map((x, j) => j === i ? { ...x, to: e.target.value } : x))} />
                          </span>
                          <IconButton icon="close" size="sm" aria-label="Rimuovi fascia"
                            onClick={() => setSlots(d.key, slots.filter((_, j) => j !== i))} />
                        </span>
                      ))}
                      <Button variant="outlined" size="sm" disabled={slots.length >= 6}
                        onClick={() => setSlots(d.key, [...slots, { from: '09:00', to: '13:00' }])}>
                        + fascia
                      </Button>
                    </div>
                  </FormGrid>
                )
              })}
              <div className="d-flex align-items-center gap-3">
                <Button variant="tonal" size="sm" disabled={saving} onClick={() => void saveSchedule()}>
                  {saving ? 'Salvataggio...' : 'Salva orario'}
                </Button>
                {notice && <span className="md-typescale-body-small text-muted">{notice}</span>}
              </div>
              <div className="md-typescale-body-small text-muted">Togliendo tutte le fasce l'operatore torna sempre disponibile.</div>

              <hr className="my-0" />
              <h3 className="md-typescale-title-medium mb-0">Nuova assenza</h3>
              <FormGrid columns={4}>
                <div>
                  <TextField label="Dal" type="date" value={absence.from}
                    onChange={e => setAbsence(a => ({ ...a, from: e.target.value }))} />
                </div>
                <div>
                  <TextField label="Al" type="date" value={absence.to}
                    onChange={e => setAbsence(a => ({ ...a, to: e.target.value }))} />
                </div>
                <div>
                  <Select label="Motivo" value={absence.reason}
                    onChange={e => setAbsence(a => ({ ...a, reason: e.target.value as AbsenceReason }))}>
                    {ABSENCE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </div>
                <div>
                  <Button variant="outlined" onClick={() => void addAbsence()}>Aggiungi assenza</Button>
                </div>
              </FormGrid>
            </>
          )}
        </div>
      </Card>

      <Card variant="outlined" padding="none">
        <CardHeader title={`Assenze in corso e future${userId !== '' ? ` di ${userLabel(userId)}` : ''}`} />
        {loading ? <LoadingState /> : userAbsences.length === 0 ? <EmptyState icon="event_busy" title="Nessuna assenza registrata" /> : (
          <DataTable variant="plain">
            <thead>
              <tr><th>Operatore</th><th>Dal</th><th>Al</th><th>Motivo</th><th /></tr>
            </thead>
            <tbody>
              {userAbsences.map(a => (
                <tr key={a.id}>
                  <td>{userLabel(a.userId)}</td>
                  <td>{a.from}</td>
                  <td>{a.to}</td>
                  <td>{a.reason}</td>
                  <td className="text-end">
                    <IconButton icon="delete" size="sm" aria-label="Elimina assenza" onClick={() => void removeAbsence(a.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </>
  )
}

// ------------------------------------------------------------------ Ritentativi

const RETRY_STATUS_TONE: Record<string, PillTone> = {
  Bozza: 'warning', Attiva: 'success', Disattivata: 'neutral',
}

interface RetryForm {
  name: string
  campaignId: number | ''
  delays: string
  exhaustionAction: RetryExhaustionAction
  recoveryCampaignId: number | ''
}

const EMPTY_RETRY: RetryForm = { name: '', campaignId: '', delays: '0, 180, 1440', exhaustionAction: 'none', recoveryCampaignId: '' }

/** "0, 180, 1440" -> [0, 180, 1440]; null se non sono tutti interi non negativi. */
function parseDelays(text: string): number[] | null {
  const parts = text.split(/[,;\s]+/).filter(p => p !== '')
  if (parts.length === 0) return null
  const out: number[] = []
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null
    out.push(Number(p))
  }
  return out
}

function formatMinutes(m: number): string {
  if (m === 0) return 'subito'
  if (m % 1440 === 0) return `${m / 1440} g`
  if (m % 60 === 0) return `${m / 60} h`
  return `${m} min`
}

function RetriesTab() {
  const askConfirm = useConfirm()
  const prompt = usePrompt()
  const snackbar = useSnackbar()
  const { campaigns, label: campaignLabel } = useCampaigns()
  const [data, setData] = useState<LeadRetryPolicyList | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<RetryForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [preview, setPreview] = useState<{ policy: LeadRetryPolicy; result: LeadRetryPreview } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await callCenterSetupApi.retryPolicies.list())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  function openNew() {
    setForm({ ...EMPTY_RETRY })
    setEditId(null)
    setSaveError('')
  }

  function openEdit(p: LeadRetryPolicy) {
    setForm({
      name: p.name, campaignId: p.campaignId ?? '', delays: p.delaysMinutes.join(', '),
      exhaustionAction: p.exhaustionAction, recoveryCampaignId: p.recoveryCampaignId ?? '',
    })
    setEditId(p.id)
    setSaveError('')
  }

  // F-100: dopo "Clona" la copia (Bozza) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(data?.items ?? [], p => openEdit(p))

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    const delays = parseDelays(form.delays)
    if (!delays || delays.length < 1 || delays.length > 10) {
      setSaveError('Indica da 1 a 10 attese in minuti, separate da virgole (0 = subito)')
      return
    }
    if (form.exhaustionAction === 'recovery_list') {
      if (form.recoveryCampaignId === '') { setSaveError('Scegli la campagna di recupero'); return }
      if (form.recoveryCampaignId === form.campaignId) { setSaveError('La campagna di recupero deve essere diversa dalla campagna della politica'); return }
    }
    const body = {
      name: form.name.trim(),
      campaignId: form.campaignId === '' ? null : form.campaignId,
      delaysMinutes: delays,
      exhaustionAction: form.exhaustionAction,
      recoveryCampaignId: form.exhaustionAction === 'recovery_list' && form.recoveryCampaignId !== '' ? form.recoveryCampaignId : null,
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await callCenterSetupApi.retryPolicies.update(editId, body)
      else await callCenterSetupApi.retryPolicies.create(body)
      setForm(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function act(action: () => Promise<unknown>, confirm?: string) {
    if (confirm && !(await askConfirm({ title: 'Conferma azione', message: confirm, confirmLabel: 'Conferma' }))) return
    try {
      await action()
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, 'Operazione non riuscita'), tone: 'error' })
    }
  }

  async function showPreview(p: LeadRetryPolicy) {
    try {
      setPreview({ policy: p, result: await callCenterSetupApi.retryPolicies.preview(p.id) })
    } catch (e) {
      snackbar.show({ message: errorText(e, 'Anteprima non riuscita'), tone: 'error' })
    }
  }

  async function activate(p: LeadRetryPolicy) {
    const reason = await prompt({
      title: 'Attiva la politica di ritentativo',
      message: `Attivare "${p.name}"? Da quel momento non si potrà più modificare, e sostituisce l'eventuale politica attiva dello stesso ambito.`,
      label: 'Motivo',
      required: true,
      confirmLabel: 'Attiva',
    })
    if (reason === null) return
    if (!reason.trim()) { snackbar.show({ message: 'Il motivo è obbligatorio', tone: 'error' }); return }
    await act(() => callCenterSetupApi.retryPolicies.activate(p.id, reason.trim()))
  }

  const scope = (p: LeadRetryPolicy) => (p.campaignId === null ? 'Predefinita' : campaignLabel(p.campaignId))
  const def = data?.defaultSchedule

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div className="text-muted md-typescale-body-small">
          Quante volte e a che distanza richiamare un lead che non risponde, per campagna, e cosa fare quando i tentativi
          finiscono. Una politica nasce in bozza: guarda l&apos;anteprima, poi attivala con un motivo. Una politica attiva
          non si modifica (si disattiva e se ne crea un&apos;altra) e ce n&apos;è al massimo una per campagna. Senza politiche
          attive vale il piano predefinito
          {def ? ` (${def.maxAttempts} tentativi: ${def.delaysMinutes.map(formatMinutes).join(', ')}${def.provisional ? ', provvisorio' : ''})` : ''}.
          Il limite &quot;Tentativi massimi&quot; della campagna, se c&apos;è, accorcia il piano. L&apos;azione a tentativi esauriti
          la esegue un controllo periodico, spento finché non viene abilitato sul server.
        </div>
        <Button variant="tonal" size="sm" className="text-nowrap" onClick={openNew}>Nuova politica</Button>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <CloneNotice text={cloneNotice} onClose={() => setCloneNotice('')} />
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : !data || data.items.length === 0 ? <EmptyState icon="phone_callback" title="Nessuna politica di ritentativo: vale il piano predefinito" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Ambito</th><th>Attese</th><th>A tentativi esauriti</th>
                <th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(p => (
                <tr key={p.id}>
                  <td className="md-typescale-emphasized-body-medium">{p.name}</td>
                  <td>{scope(p)}</td>
                  <td>
                    <span className="md-typescale-body-small">{p.delaysMinutes.length} tentativi: {p.delaysMinutes.map(formatMinutes).join(' → ')}</span>
                  </td>
                  <td>
                    {RETRY_ACTIONS.find(a => a.value === p.exhaustionAction)?.label ?? p.exhaustionAction}
                    {p.exhaustionAction === 'recovery_list' && p.recoveryCampaignId !== null && (
                      <div className="md-typescale-body-small text-muted">{campaignLabel(p.recoveryCampaignId)}</div>
                    )}
                  </td>
                  <td>
                    <StatusPill tone={RETRY_STATUS_TONE[p.status] ?? 'neutral'}>{p.status}</StatusPill>
                    {p.reason && <div className="md-typescale-body-small text-muted">{p.reason}</div>}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <CloneConfigurationButton
                        entityLabel="politica di ritentativo"
                        url={`/api/lead-retry-policies/${p.id}/clone`}
                        defaultName={p.name}
                        extraFields={[{ key: 'campaignId', label: 'Id campagna (vuoto = stessa della politica)', placeholder: 'es. 12' }]}
                        note="Si copiano attese e azione a tentativi esauriti. La copia nasce in bozza."
                        onCloned={async id => { await load(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />
                      <Button variant="outlined" size="sm" onClick={() => void showPreview(p)}>Anteprima</Button>
                      {p.status === 'Bozza' && (
                        <>
                          <Button variant="outlined" size="sm" onClick={() => openEdit(p)}>Modifica</Button>
                          <Button variant="outlined" size="sm" onClick={() => void activate(p)}>Attiva</Button>
                          <Button variant="outlined" size="sm"
                            onClick={() => void act(() => callCenterSetupApi.retryPolicies.remove(p.id), `Eliminare la bozza "${p.name}"?`)}>
                            Elimina
                          </Button>
                        </>
                      )}
                      {p.status === 'Attiva' && (
                        <Button variant="outlined" size="sm"
                          onClick={() => void act(() => callCenterSetupApi.retryPolicies.deactivate(p.id), `Disattivare "${p.name}"? Tornerà il piano predefinito.`)}>
                          Disattiva
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {preview && (
        <Card variant="outlined">
          <CardHeader
            title={`Anteprima di "${preview.policy.name}"`}
            actions={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setPreview(null)} />}
          />
          <CardContent>
            <p className="mb-2">
              Con questo piano ({preview.result.maxAttempts} tentativi) risultano esauriti{' '}
              <strong>{preview.result.exhaustedCount}</strong> lead; l&apos;azione scatterebbe su{' '}
              <strong>{preview.result.actionCount}</strong>. Non cambia nulla finché la politica non è attiva e il controllo
              periodico non è abilitato.
            </p>
            {preview.result.leadIds.length > 0 && (
              <div className="md-typescale-body-small">
                Primi {preview.result.leadIds.length}:{' '}
                {preview.result.leadIds.map(id => (
                  <Link key={id} to={`/leads/${id}`} className="me-2">#{id}</Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {form && (
        <Modal title={editId !== null ? 'Modifica bozza della politica' : 'Nuova politica di ritentativo'} saving={saving} error={saveError}
          onClose={() => { setForm(null); setEditId(null) }} onSave={() => void save()}>
          <FormGrid>
            <TextField className="md-span-full" label="Nome *" value={form.name} maxLength={100}
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <Select label="Si applica a" value={form.campaignId}
              onChange={e => setForm(f => f && ({ ...f, campaignId: e.target.value === '' ? '' : Number(e.target.value) }))}>
              <option value="">Predefinita (campagne senza una politica propria)</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <TextField className="md-span-full" label="Attese prima di ogni tentativo, in minuti *" value={form.delays}
              supportingText="Da 1 a 10 numeri separati da virgole: il primo è l'attesa prima del 1° tentativo (0 = subito), il secondo prima del 2° dopo il 1°, e così via. Esempio: 0, 180, 1440 = subito, dopo 3 ore, dopo un giorno. Il numero di attese è il numero massimo di tentativi."
              onChange={e => setForm(f => f && ({ ...f, delays: e.target.value }))} />
            <Select label="A tentativi esauriti" value={form.exhaustionAction}
              supportingText={RETRY_ACTIONS.find(a => a.value === form.exhaustionAction)?.hint}
              onChange={e => setForm(f => f && ({ ...f, exhaustionAction: e.target.value as RetryExhaustionAction }))}>
              {RETRY_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </Select>
            {form.exhaustionAction === 'recovery_list' && (
              <Select label="Campagna di recupero *" value={form.recoveryCampaignId}
                onChange={e => setForm(f => f && ({ ...f, recoveryCampaignId: e.target.value === '' ? '' : Number(e.target.value) }))}>
                <option value="">Scegli…</option>
                {campaigns.filter(c => c.id !== form.campaignId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            )}
          </FormGrid>
        </Modal>
      )}
    </>
  )
}

// ------------------------------------------------------- Tempi di contatto (F-143, REQ-073-001)

interface SpeedRow { sourceCode: string; label: string; minutes: string; confirmed: boolean; isDefault: boolean }

/**
 * Minuti entro cui un lead di ogni fonte deve avere il primo tentativo di chiamata. Senza interventi valgono i numeri
 * predefiniti, provvisori; chi li conferma li rende definitivi. Scrive solo l'Admin (il server risponde 403 agli altri).
 */
function SpeedTargetsTab() {
  const [data, setData] = useState<LeadSpeedTargets | null>(null)
  const [rows, setRows] = useState<SpeedRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')

  const apply = useCallback((d: LeadSpeedTargets) => {
    setData(d)
    setRows(d.targets.map(t => ({
      sourceCode: t.sourceCode, label: t.label, minutes: String(t.minutes), confirmed: t.confirmed, isDefault: t.isDefault,
    })))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      apply(await callCenterSetupApi.speedTargets.get())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [apply])
  useEffect(() => { void load() }, [load])

  const update = (code: string, patch: Partial<SpeedRow>) =>
    setRows(rs => rs.map(r => (r.sourceCode === code ? { ...r, ...patch } : r)))

  async function save() {
    const targets = rows.map(r => ({ sourceCode: r.sourceCode, minutes: parseInt(r.minutes, 10), confirmed: r.confirmed }))
    if (targets.some(t => !Number.isInteger(t.minutes) || t.minutes < 1 || t.minutes > 10080)) {
      setError('I minuti devono essere un numero intero da 1 a 10080')
      return
    }
    setSaving(true)
    setError('')
    setNotice('')
    try {
      apply(await callCenterSetupApi.speedTargets.put(targets))
      setNotice('Tempi di contatto salvati.')
    } catch (e) {
      setError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="text-muted md-typescale-body-small">
        Entro quanti minuti dall&apos;arrivo di un lead deve esserci il primo tentativo di chiamata, per fonte. Report,
        cruscotto del responsabile e lista dei nuovi lead usano questi valori. Finché una fonte non è confermata i tempi
        restano provvisori.
      </div>
      {data && (
        <Callout tone={data.provisional ? 'warning' : 'success'}>
          {data.provisional ? 'Tempi provvisori: almeno una fonte non è confermata.' : 'Tutti i tempi sono confermati.'}
        </Callout>
      )}
      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : (
          <DataTable variant="plain">
            <thead>
              <tr><th>Fonte</th><th>Minuti</th><th>Confermato</th><th>Origine</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.sourceCode}>
                  <td className="md-typescale-emphasized-body-medium">{r.label}</td>
                  <td>
                    <TextField label={`Minuti per ${r.label}`} labelHidden type="number" min={1} max={10080} value={r.minutes}
                      onChange={e => update(r.sourceCode, { minutes: e.target.value })} />
                  </td>
                  <td>
                    <Checkbox id={`speed-${r.sourceCode}`} label="Confermato" checked={r.confirmed}
                      onChange={e => update(r.sourceCode, { confirmed: e.target.checked })} />
                  </td>
                  <td>{r.isDefault ? <StatusPill tone="neutral">Predefinito</StatusPill> : <StatusPill tone="primary">Configurato</StatusPill>}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
      <div>
        <Button variant="filled" disabled={saving || loading} onClick={() => void save()}>
          {saving ? 'Salvataggio...' : 'Salva tempi'}
        </Button>
      </div>
    </>
  )
}

// --------------------------------------------------------------------- Pagina

export function CallCenterSetupPage() {
  const [tab, setTab] = useState<Tab>('scripts')
  return (
    <Page>
      <PageHeader
        title="Script e disponibilità"
        icon="settings_phone"
        tabs={
          <Tabs
            aria-label="Sezioni del call center"
            items={TABS.map(t => ({ id: t.key, label: t.label }))}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />
      {tab === 'scripts' && <ScriptsTab />}
      {tab === 'forms' && <FormsTab />}
      {tab === 'availability' && <AvailabilityTab />}
      {tab === 'retries' && <RetriesTab />}
      {tab === 'speed' && <SpeedTargetsTab />}
    </Page>
  )
}
